<template>
  <div
    class="file-uploader"
    :class="{ 'is-dragging': isDragging }"
    tabindex="0"
    role="presentation"
    @click="openFileDialog"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <input
      ref="fileInput"
      type="file"
      :accept="accept"
      :multiple="multiple"
      tabindex="-1"
      class="hidden-input"
      @change="onFileChange"
    />

    <div class="icon-box">
      <!-- icon (inline svg to avoid deps) -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v4" />
        <path d="M14 2v4a2 2 0 0 0 2 2h4" />
        <path d="M3 15h6" />
        <path d="M6 12v6" />
      </svg>
    </div>

    <p class="title">Click or drag files to upload</p>
    <p class="subtitle">Up to {{ maxSizeMB }} MB each.</p>

    <div class="file-types">
      <span v-for="ext in visibleExtensions" :key="ext">{{ ext }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: "FileUploader",

  props: {
    accept: {
      type: String,
      default:
        "application/epub+zip,.epub,application/pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,text/plain,.txt,text/html,.html,text/markdown,.md",
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    maxSizeMB: {
      type: Number,
      default: 21,
    },
  },

  data() {
    return {
      isDragging: false,
    };
  },

  computed: {
    visibleExtensions() {
      return this.accept
        .split(",")
        .filter((v) => v.startsWith("."))
        .map((v) => v.replace(".", ""));
    },
  },

  methods: {
    openFileDialog() {
      this.$refs.fileInput.click();
    },

    onDragEnter() {
      this.isDragging = true;
    },

    onDragOver() {
      this.isDragging = true;
    },

    onDragLeave() {
      this.isDragging = false;
    },

    onDrop(e) {
      this.isDragging = false;
      this.handleFiles(e.dataTransfer.files);
    },

    onFileChange(e) {
      this.handleFiles(e.target.files);
      e.target.value = ""; // reset input
    },

    handleFiles(fileList) {
      const files = Array.from(fileList);
      const validFiles = [];

      files.forEach((file) => {
        const isValidSize =
          file.size <= this.maxSizeMB * 1024 * 1024;

        if (isValidSize) {
          validFiles.push(file);
        }
      });

      this.$emit("change", validFiles);
    },
  },
};
</script>

<style scoped lang="scss">
.file-uploader {
  cursor: pointer;
  border-radius: 12px;
  border: 1px dashed rgba(0, 0, 0, 0.12);
  background: rgba(0, 0, 0, 0.02);
  padding: 20px;
  text-align: center;
  transition: all 0.2s ease;
  outline: none;
  

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  &.is-dragging {
    border-color: #8d25f6;
    background: rgba(141, 37, 246, 0.08);
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(141, 37, 246, 0.35);
  }
}

.hidden-input {
  display: none;
}

.icon-box {
  height: 48px;
  width: 48px;
  margin: 8px auto 12px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
}

.title {
  font-size: 14px;
  font-weight: 600;
  color: #111;
}

.subtitle {
  font-size: 13px;
  color: #666;
  margin-top: 4px;
}

.file-types {
  margin-top: 12px;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;

  span {
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    background: #fff;
    color: #555;
  }
}
</style>
